import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Briefcase,
  ArrowUpRight,
  CheckCircle2,
  BadgeDollarSign,
  Workflow,
  Layers3,
  Filter,
  Sparkles,
  BookOpen,
} from "lucide-react";
import { businessGuides } from "../businessGuides";

const categories = ["All", "Start", "Pricing", "Management", "Marketing", "Scaling"];

function getTagClasses(tag: string) {
  switch (tag) {
    case "Must Read":
      return "border-dtf-accent/25 bg-dtf-accent/10 text-dtf-accent";
    case "Money":
      return "border-green-500/25 bg-green-500/10 text-green-400";
    case "Beginner":
      return "border-dtf-blue/25 bg-dtf-blue/10 text-dtf-blue";
    case "Advanced":
      return "border-purple-500/25 bg-purple-500/10 text-purple-400";
    case "Workflow":
      return "border-cyan-500/25 bg-cyan-500/10 text-cyan-400";
    default:
      return "border-white/10 bg-black/20 text-dtf-text-muted";
  }
}

function BluePanel({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={[
        "relative overflow-hidden rounded-[28px] border border-dtf-accent/20",
        "bg-[radial-gradient(circle_at_top_center,rgba(37,99,235,0.18),transparent_42%),linear-gradient(180deg,rgba(7,16,35,0.98)_0%,rgba(6,14,30,0.98)_100%)]",
        "shadow-[0_0_0_1px_rgba(245,158,11,0.05),0_0_34px_rgba(37,99,235,0.16)]",
        className,
      ].join(" ")}
    >
      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(90deg,rgba(37,99,235,0.08),transparent_30%,transparent_70%,rgba(37,99,235,0.06))]" />
      <div className="pointer-events-none absolute inset-0 rounded-[28px] ring-1 ring-inset ring-white/5" />
      {children}
    </div>
  );
}

function StatPill({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div className="rounded-2xl border border-white/10 bg-black/20 px-4 py-3">
      <div className="mb-1 flex items-center gap-2 text-xs uppercase tracking-[0.16em] text-dtf-text-muted">
        <span className="text-dtf-accent">{icon}</span>
        <span>{label}</span>
      </div>
      <div className="text-base font-semibold text-white">{value}</div>
    </div>
  );
}

function FeatureCard({
  icon,
  title,
  description,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
}) {
  return (
    <BluePanel>
      <div className="p-5">
        <div className="mb-3 flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl border border-white/10 bg-black/20 text-dtf-accent">
            {icon}
          </div>
          <h2 className="text-lg font-semibold text-white">{title}</h2>
        </div>
        <p className="text-sm leading-7 text-dtf-text-muted">{description}</p>
      </div>
    </BluePanel>
  );
}

function CategoryChip({
  active,
  children,
  onClick,
}: {
  active: boolean;
  children: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`rounded-full border px-4 py-2 text-sm font-medium transition ${
        active
          ? "border-dtf-blue/35 bg-dtf-blue/10 text-white shadow-[0_0_18px_rgba(59,130,246,0.16)]"
          : "border-white/10 bg-black/20 text-dtf-text-muted hover:border-dtf-blue/30 hover:text-white"
      }`}
    >
      {children}
    </button>
  );
}

export default function BusinessBuilderPage() {
  const [selectedCategory, setSelectedCategory] = useState("All");

  const filteredGuides = useMemo(() => {
    if (selectedCategory === "All") return businessGuides;
    return businessGuides.filter((g) => g.category === selectedCategory);
  }, [selectedCategory]);

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 text-white sm:px-6 lg:px-8">
      <BluePanel className="mb-6">
        <div className="p-6 sm:p-7">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
            <div className="max-w-3xl">
              <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-dtf-accent/20 bg-dtf-blue/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.22em] text-dtf-blue">
                <Briefcase className="h-3.5 w-3.5" />
                Business Growth System
              </div>

              <h1 className="mb-3 text-4xl font-extrabold tracking-tight text-white sm:text-5xl">
                Business Builder
              </h1>

              <p className="text-lg leading-8 text-dtf-text">
                Learn how to build, price, manage, market, and scale your DTF business
                with practical guidance designed to help you make smarter decisions,
                avoid common mistakes, and grow with more confidence.
              </p>
            </div>

            <div className="grid gap-3 sm:min-w-[320px] sm:grid-cols-2">
              <StatPill
                icon={<Layers3 className="h-4 w-4" />}
                label="Total Guides"
                value={`${businessGuides.length}`}
              />
              <StatPill
                icon={<Filter className="h-4 w-4" />}
                label="Showing"
                value={`${filteredGuides.length}`}
              />
              <StatPill
                icon={<BookOpen className="h-4 w-4" />}
                label="Focus"
                value={selectedCategory}
              />
              <StatPill
                icon={<Sparkles className="h-4 w-4" />}
                label="Library"
                value="Premium"
              />
            </div>
          </div>

          <div className="mt-6 flex flex-wrap gap-3 text-sm">
            <div className="rounded-full border border-white/10 bg-black/20 px-4 py-2 text-white">
              Built for real DTF shops
            </div>
            <div className="rounded-full border border-white/10 bg-black/20 px-4 py-2 text-white">
              Pricing, workflow, growth, and systems
            </div>
            <div className="rounded-full border border-white/10 bg-black/20 px-4 py-2 text-white">
              Practical business guidance
            </div>
          </div>
        </div>
      </BluePanel>

      <div className="mb-6 grid gap-4 md:grid-cols-3">
        <FeatureCard
          icon={<CheckCircle2 className="h-5 w-5" />}
          title="Build smarter"
          description="Learn the foundational decisions that make your shop easier to run and harder to derail."
        />
        <FeatureCard
          icon={<BadgeDollarSign className="h-5 w-5" />}
          title="Price with confidence"
          description="Stop guessing your prices and understand profit, margins, hidden costs, and rush work."
        />
        <FeatureCard
          icon={<Workflow className="h-5 w-5" />}
          title="Create better systems"
          description="Tighten your workflow, reduce chaos, and make growth more repeatable instead of stressful."
        />
      </div>

      <BluePanel className="mb-6">
        <div className="border-b border-white/8 px-6 py-5">
          <div className="mb-2 inline-flex items-center gap-2 rounded-full border border-dtf-blue/25 bg-dtf-blue/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.22em] text-dtf-blue">
            <Filter className="h-3.5 w-3.5" />
            Categories
          </div>

          <h2 className="text-3xl font-bold tracking-tight text-white">
            Browse business guides
          </h2>

          <p className="mt-2 max-w-3xl text-dtf-text-muted">
            Filter by category to focus on startup decisions, pricing, management,
            marketing, or scaling.
          </p>
        </div>

        <div className="flex flex-col gap-4 px-6 py-5 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap gap-3">
            {categories.map((cat) => (
              <CategoryChip
                key={cat}
                active={selectedCategory === cat}
                onClick={() => setSelectedCategory(cat)}
              >
                {cat}
              </CategoryChip>
            ))}
          </div>

          <div className="text-sm text-dtf-text-muted">
            Showing{" "}
            <span className="font-semibold text-white">{filteredGuides.length}</span>{" "}
            {filteredGuides.length === 1 ? "guide" : "guides"}
            {selectedCategory !== "All" ? (
              <>
                {" "}in <span className="font-semibold text-dtf-blue">{selectedCategory}</span>
              </>
            ) : null}
          </div>
        </div>
      </BluePanel>

      <BluePanel className="mb-6">
        <div className="flex items-center justify-between gap-4 px-6 py-5">
          <div>
            <div className="text-sm text-dtf-text-muted">Showing results</div>
            <div className="text-2xl font-bold text-white">
              {filteredGuides.length} guide{filteredGuides.length === 1 ? "" : "s"}
            </div>
          </div>

          <div className="rounded-full border border-dtf-blue/25 bg-dtf-blue/10 px-4 py-2 text-sm font-semibold uppercase tracking-[0.18em] text-dtf-blue">
            Business library visible
          </div>
        </div>
      </BluePanel>

      <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
        {filteredGuides.map((guide) => (
          <Link
            key={guide.id}
            to={`/business-builder/${guide.id}`}
            className="group relative flex h-full flex-col justify-between overflow-hidden rounded-[26px] border border-white/10 bg-[radial-gradient(circle_at_top_center,rgba(37,99,235,0.14),transparent_48%),linear-gradient(180deg,rgba(9,21,44,0.98)_0%,rgba(7,15,31,0.98)_100%)] p-5 transition duration-200 hover:-translate-y-0.5 hover:border-dtf-blue/30 hover:shadow-[0_0_26px_rgba(37,99,235,0.18)]"
          >
            <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(90deg,rgba(37,99,235,0.06),transparent_35%,transparent_65%,rgba(37,99,235,0.04))]" />

            <div className="relative">
              <div className="mb-4 flex items-start justify-between gap-4">
                <div className="inline-flex items-center rounded-full border border-dtf-accent/20 bg-black/20 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-dtf-accent">
                  {guide.category}
                </div>

                <ArrowUpRight className="h-5 w-5 flex-shrink-0 text-dtf-text-muted transition-colors group-hover:text-dtf-blue" />
              </div>

              <h2 className="mb-3 text-2xl font-bold leading-tight tracking-tight text-white transition-colors group-hover:text-dtf-blue">
                {guide.title}
              </h2>

              <p className="mb-5 text-sm leading-7 text-dtf-text">
                {guide.summary}
              </p>

              <div className="mb-5 flex flex-wrap gap-2">
                {(guide.tags ?? []).map((tag) => (
                  <span
                    key={tag}
                    className={`rounded-full border px-2.5 py-1 text-xs font-medium ${getTagClasses(tag)}`}
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>

            <div className="relative flex items-center justify-between border-t border-white/8 pt-4">
              <span className="text-sm font-medium text-white transition-colors group-hover:text-dtf-blue">
                Open Guide
              </span>

              <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.16em] text-dtf-text-muted transition-colors group-hover:text-dtf-blue">
                <Sparkles className="h-3.5 w-3.5" />
                Read More
              </span>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}